import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-pine-900/15 bg-white px-4 py-3 text-sm text-pine-950 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-stone-400 focus:border-pine-700 focus:ring-2 focus:ring-pine-700/15";

const INFO = [
  { icon: Mail, label: "Email", value: "spellingbeetherapy@gmail.com" },
  { icon: Phone, label: "Phone / WhatsApp", value: "+91 99018 87986" },
  { icon: MapPin, label: "Location", value: "Serving North Bengaluru — all sessions delivered in-school" },
];

export const Contact = ({ onBook }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Message sent", { description: "We will reply within one business day." });
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send your message", { description: "Please try again or email spellingbeetherapy@gmail.com." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="mb-4 font-mono text-xs font-medium uppercase tracking-[0.28em] text-pine-700">Say hello</p>
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-pine-950 sm:text-3xl lg:text-4xl">
            The first word starts here.
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-stone-600 sm:text-base">
            Whether you're a principal planning next term, a coordinator with
            students in mind, or a parent looking for support — write to us. We
            reply within one business day.
          </p>
          <div className="mt-10 space-y-5">
            {INFO.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sand text-pine-700">
                  <Icon size={16} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</p>
                  <p className="mt-0.5 text-sm font-medium text-pine-950">{value}</p>
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="booking-modal-trigger"
            onClick={onBook}
            className="mt-10 text-sm font-semibold text-clay underline-offset-4 transition-colors hover:text-pine-900 hover:underline"
          >
            Prefer to talk? Book a free 15-minute consult →
          </button>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-pine-900/10 bg-sand p-8 sm:p-10"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input data-testid="contact-form-name" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
              <input data-testid="contact-form-email" className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
            </div>
            <textarea
              data-testid="contact-form-message"
              className={`${inputCls} mt-4 min-h-[150px] resize-none`}
              placeholder="Tell us a little about what brings you here…"
              value={form.message}
              onChange={set("message")}
            />
            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={!valid || sending}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-pine-900 py-4 text-sm font-semibold text-cream transition-[background-color,opacity,transform] duration-300 hover:-translate-y-0.5 hover:bg-pine-800 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-10"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
